import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import uuid from 'uuid/v4';
import produce from 'immer';
import {
  Button,
  MenuItem,
  TextField,
  InputLabel,
  FormControl,
  Typography,
  Select,
} from '@material-ui/core';
import * as yup from 'yup';
import _ from 'lodash';
import { makeStyles } from '@material-ui/styles';
import {
  deleteAttachment,
  uploadAttachment,
  changeDraftIncident,
} from './ducks';
import ExpansionCard from '../../components/cards/ExpansionCard';
import { useForm } from '../../utils/hooks';
import RichEditor from '../../components/forms/RichEditor';
import {
  IncidentVehicleDef,
  LookupDef,
  PropertyOwnerDef,
  StateProvinceDef,
  IncidentAttachmentDef,
} from '../../components/propTypeDefs';
import { uuidPattern, AttachmentType } from '../../constants';
import { getVehicleName } from '../../utils/nameUtil';
import IncidentAttachmentItem from './IncidentAttachmentItem';
import AddAttachmentDialog from '../../components/dialogs/AddAttachmentDialog';
import AttachedImageViewer from '../../components/dialogs/AttachedImageViewer';
import { getAzureFileUrl, downloadFromUrl } from '../../utils/fileUtil';
import Auth from '../../auth/Auth';
import HelpButton from '../../components/buttons/HelpButton';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'wrap',
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 180,
    [theme.breakpoints.down('sm')]: {
      flexGrow: 1,
    },
    // width: '100%',
  },
  formControlShort: {
    margin: theme.spacing(1),
    minWidth: 150,
    [theme.breakpoints.down('sm')]: {
      flexGrow: 1,
    },
    // width: '100%',
  },
  formControlLong: {
    margin: theme.spacing(1),
    [theme.breakpoints.up('md')]: {
      minWidth: 400,
    },
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  formControlFull: {
    margin: theme.spacing(1),
    width: '100%',
  },
  subCommentForm: {
    flexGrow: 1,
    margin: theme.spacing(1, 0),
    width: '100%',
  },
  paper: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(3),
    textAlign: 'left',
    color: theme.palette.text.secondary,
  },
  lineItem: {
    display: 'flex',
    flexDirection: 'row',
    // alignItems: 'center',
    // height: 80,
    flexWrap: 'wrap',
  },
  vehicleName: {
    margin: theme.spacing(1),
  },

  itemButton: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  deleteButton: {
    backgroundColor: theme.palette.error.dark,
    color: 'white',
  },
  expansionCardContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  attachmentRoot: {
    width: '100%',
    // flexGrow: 1,
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    overflow: 'hidden',
    backgroundColor: theme.palette.background.paper,
  },
  attachmentItem: {
    padding: theme.spacing(1),
  },
  addAttachmentButton: {
    backgroundColor: theme.palette.primary.main,
    marginRight: theme.spacing(1),
  },
  labelWithHelpIcon: {
    display: 'flex',
    alignItems: 'center',
  },
  emptyItem: {
    minHeight: '30px',
  },
}));

const schema = yup.object().shape({
  ownerPersonId: yup
    .string()
    .nullable()
    .matches(uuidPattern, {
      message: 'invalid ownerPersonId',
    }),
  licensePlate: yup
    .string()
    .nullable()
    .max(20),
  stateProvince: yup
    .string()
    .nullable()
    .max(50),
  maker: yup
    .string()
    .nullable()
    .max(45),
  model: yup
    .string()
    .nullable()
    .max(45),
  color: yup
    .string()
    .nullable()
    .max(45),
  vin: yup
    .string()
    .nullable()
    .max(30),
  involvementId: yup
    .string()
    .nullable()
    .matches(uuidPattern, {
      message: 'invalid involvementId',
      excludeEmptyString: true,
    }),
  comments: yup
    .string()
    .nullable()
    .max(500),
});

const IncidentVehicleItem = props => {
  const classes = useStyles();
  const { expanded, vehicle, states } = props;
  const {
    formValues,
    updateFormValues,
    hasFormError,
    formHelpText,
    handleFormChange,
    handleFormBlur,
  } = useForm({
    validationSchema: schema,
    onSubmit: (values, errors) => {},
  });

  const [state, setState] = React.useState({
    attachments: props.attachments,
    selectedAttachment: undefined,
    viewerOpen: false,
  });

  const [addAttachmentDialogOpen, setAddAttachmentDialogOpen] = React.useState({
    open: false,
    uloading: false,
  });

  React.useEffect(() => {
    updateFormValues(vehicle);
  }, []);

  React.useEffect(() => {
    setState({
      attachments: props.attachments,
      selectedAttachment: undefined,
      viewerOpen: false,
    });
  }, [props.attachments]);

  React.useEffect(() => {
    !_.isEmpty(formValues) && props.onChange(formValues);
  }, [formValues]);

  const inputHandleChange = event => {
    handleFormChange(event);
  };

  const onCardExpanded = expand => {
    props.onCardExpanded(expand, props.vehicle.id);
  };
  const raiseFormEvent = (name, value) => {
    inputHandleChange({ target: { name, value } });
  };
  const handleCommentsChange = value => {
    raiseFormEvent('comments', value);
  };

  const deleteVehicle = () => {
    state.attachments.forEach(attachment => {
      if (attachment.incidentVehicleId) {
        props.deleteAttachment(attachment);
      }
    });

    props.onDeleted(props.vehicle.id);
  };

  const vehicleName = getVehicleName(vehicle);

  const onAttachmentDeleted = incident => {
    if (incident) {
      props.deleteAttachment(incident);
    }
  };

  const openAddAttachmentDialog = () => {
    setState({
      ...state,
      selectedAttachment: {
        id: uuid(),
        incidentId: props.vehicle.incidentId,
        incidentVehicleId: props.vehicle.id,
        name: `Attachment ${state.attachments.length + 1}`,
        comments: '',
      },
    });
    setAddAttachmentDialogOpen({ open: true, uploading: false });
  };

  const addAttachment = formData => {
    setAddAttachmentDialogOpen({ open: true, uploading: true });
    props
      .uploadAttachment(formData)
      .then(() => {
        setAddAttachmentDialogOpen({ open: false, uploading: false });
      })
      .catch(err => {
        setAddAttachmentDialogOpen({ open: true, uploading: false });
      });
  };
  const handleAttachmentDownload = attachment => {
    if (
      attachment.attachmentType === AttachmentType.image ||
      attachment.attachmentType === AttachmentType.movie ||
      attachment.attachmentType === AttachmentType.audio ||
      attachment.attachmentType === AttachmentType.txt ||
      attachment.attachmentType === AttachmentType.pdf
    ) {
      setState({ ...state, viewerOpen: true, selectedAttachment: attachment });
    } else {
      if (!Auth.currentUser) return;
      const url = getAzureFileUrl(
        Auth.currentUser.accountId,
        attachment.fileName,
        false,
        attachment.sasKey,
        attachment.thumbnailSasKey
      );
      downloadFromUrl(url, attachment.name);
    }
  };
  const handleViewerClose = () => {
    setState({ ...state, viewerOpen: false, selectedAttachment: undefined });
  };

  const handleAttachmentChange = model => {
    const nextState = produce(state, draft => {
      const found = state.attachments.find(x => x.id === model.id);
      Object.assign(found, model);
    });
    setState(nextState);
    props.changeDraftIncident({ attachments: nextState.attachments });
  };

  return (
    <ExpansionCard
      title={vehicleName}
      theme="sub"
      expanded={expanded}
      onChange={onCardExpanded}
    >
      <div className={classes.expansionCardContainer}>
        <div className={classes.expansionCardItem}>
          <div className={classes.lineItem}>
            <FormControl className={classes.formControlLong}>
              <InputLabel shrink htmlFor="propertyOf-label-placeholder">
                Property of
              </InputLabel>
              <Select
                value={formValues.ownerPersonId || ''}
                onChange={inputHandleChange}
                displayEmpty
                name="ownerPersonId"
                className={classes.selectEmpty}
              >
                <MenuItem value="" className={classes.emptyItem} />
                {props.propertyOwners.map(owner => {
                  return (
                    <MenuItem value={owner.id} key={owner.id}>
                      {`${owner.name}`}
                      {` (${
                        owner.personType === 0 ? 'Person' : 'Legal entity'
                      })`}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </div>
          <div className={classes.lineItem}>
            <FormControl className={classes.formControlShort}>
              <TextField
                name="licensePlate"
                label="License plate #"
                value={formValues.licensePlate || ''}
                onChange={inputHandleChange}
                margin="none"
                InputLabelProps={{
                  shrink: true,
                }}
                onBlur={handleFormBlur}
                error={hasFormError('licensePlate')}
                helperText={formHelpText('licensePlate')}
                inputProps={{ autoComplete: 0 }}
              />
            </FormControl>
            <FormControl className={classes.formControlShort}>
              <TextField
                name="stateProvince"
                label="State/Province"
                value={formValues.stateProvince || ''}
                onChange={inputHandleChange}
                margin="none"
                InputLabelProps={{
                  shrink: true,
                }}
                onBlur={handleFormBlur}
                error={hasFormError('stateProvince')}
                helperText={formHelpText('stateProvince')}
                inputProps={{ autoComplete: 0 }}
                select
              >
                {states.map(x => (
                  <MenuItem key={x.code} value={x.code}>
                    {`${x.name}, ${x.countryCode}`}
                  </MenuItem>
                ))}
              </TextField>
            </FormControl>
            <FormControl className={classes.formControlShort}>
              <TextField
                name="maker"
                label="Maker"
                value={formValues.maker || ''}
                onChange={inputHandleChange}
                margin="none"
                InputLabelProps={{
                  shrink: true,
                }}
                onBlur={handleFormBlur}
                error={hasFormError('maker')}
                helperText={formHelpText('maker')}
                inputProps={{ autoComplete: 0 }}
              />
            </FormControl>
            <FormControl className={classes.formControlShort}>
              <TextField
                name="model"
                label="Model"
                value={formValues.model || ''}
                onChange={inputHandleChange}
                margin="none"
                InputLabelProps={{
                  shrink: true,
                }}
                onBlur={handleFormBlur}
                error={hasFormError('model')}
                helperText={formHelpText('model')}
                inputProps={{ autoComplete: 0 }}
              />
            </FormControl>
            <FormControl className={classes.formControlShort}>
              <TextField
                name="color"
                label="Color"
                value={formValues.color || ''}
                onChange={inputHandleChange}
                margin="none"
                InputLabelProps={{
                  shrink: true,
                }}
                onBlur={handleFormBlur}
                error={hasFormError('color')}
                helperText={formHelpText('color')}
                inputProps={{ autoComplete: 0 }}
              />
            </FormControl>
            <FormControl className={classes.formControlShort}>
              <TextField
                name="vin"
                label="VIN #"
                value={formValues.vin || ''}
                onChange={inputHandleChange}
                margin="none"
                InputLabelProps={{
                  shrink: true,
                }}
                onBlur={handleFormBlur}
                error={hasFormError('vin')}
                helperText={formHelpText('vin')}
                inputProps={{ autoComplete: 0 }}
              />
            </FormControl>
            <FormControl className={classes.formControl}>
              <InputLabel shrink htmlFor="involvement-label-placeholder">
                Involvement
              </InputLabel>
              <Select
                value={formValues.involvementId || ''}
                onChange={inputHandleChange}
                // input={
                //   <Input
                //     // name="involvementId"
                //     id="involvement-label-placeholder"
                //   />
                // }
                displayEmpty
                name="involvementId"
                className={classes.selectEmpty}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {props.involveLookups.map(item => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
              {/* <FormHelperText>Label + placeholder</FormHelperText> */}
            </FormControl>
          </div>
          <Typography variant="h6">Comments</Typography>
          <FormControl className={classes.subCommentForm}>
            <RichEditor
              height="low"
              onChange={handleCommentsChange}
              defaultValue={formValues.comments}
            />
          </FormControl>
          <Typography variant="h6" className={classes.labelWithHelpIcon}>
            Attachments
            <HelpButton message="Attach files related to the involved vehicles." />
          </Typography>
          <div className={classes.attachmentRoot}>
            {state.attachments &&
              state.attachments
                .filter(x => x.incidentVehicleId)
                .map(_attachment => {
                  return (
                    <div
                      className={classes.attachmentItem}
                      key={_attachment.id}
                    >
                      <IncidentAttachmentItem
                        editable
                        attachment={_attachment}
                        onDeleted={onAttachmentDeleted}
                        onChange={handleAttachmentChange}
                        onDownload={handleAttachmentDownload}
                        attachments={props.attachments}
                      />
                    </div>
                  );
                })}
          </div>
          <div className={classes.itemButton}>
            <Button
              variant="contained"
              className={classes.addAttachmentButton}
              onClick={openAddAttachmentDialog}
            >
              Add attachment
            </Button>
            <Button
              variant="contained"
              className={classes.deleteButton}
              onClick={deleteVehicle}
            >
              Delete
            </Button>
          </div>
        </div>
      </div>
      {addAttachmentDialogOpen.open && (
        <AddAttachmentDialog
          open={addAttachmentDialogOpen.open}
          uploading={addAttachmentDialogOpen.uploading}
          onCancel={() => {
            setAddAttachmentDialogOpen({ open: false, uploading: false });
          }}
          onAttachmentAdded={addAttachment}
          attachment={state.selectedAttachment}
          uploadLimit={props.uploadLimit}
        />
      )}
      {state.selectedAttachment && (
        <AttachedImageViewer
          attachment={state.selectedAttachment}
          open={state.viewerOpen}
          onClose={handleViewerClose}
        />
      )}
    </ExpansionCard>
  );
};

IncidentVehicleItem.propTypes = {
  vehicle: IncidentVehicleDef.isRequired,
  expanded: PropTypes.bool,
  onCardExpanded: PropTypes.func,
  onDeleted: PropTypes.func,
  onChange: PropTypes.func,
  involveLookups: PropTypes.arrayOf(LookupDef),
  propertyOwners: PropTypes.arrayOf(PropertyOwnerDef),
  states: PropTypes.arrayOf(StateProvinceDef),
  attachments: PropTypes.arrayOf(IncidentAttachmentDef),
  deleteAttachment: PropTypes.func,
  uploadAttachment: PropTypes.func,
  changeDraftIncident: PropTypes.func,
  uploadLimit: PropTypes.number,
};

IncidentVehicleItem.defaultProps = {
  expanded: true,
  involveLookups: [],
  propertyOwners: [],
  onCardExpanded: () => {},
  onDeleted: () => {},
  onChange: () => {},
  states: [],
  attachments: [],
  deleteAttachment: () => {},
  changeDraftIncident: () => {},
};

const mapStateToProps = ({ common }) => ({
  uploadLimit: common.fileSizeLimit.maxUploadFileSize,
});
const mapDispatchToProps = {
  deleteAttachment,
  uploadAttachment,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(IncidentVehicleItem);
